import {
    forwardRef,
    useCallback,
    useEffect,
    useImperativeHandle,
    useRef,
    useState,
} from 'react'
import {
    MdAdd,
    MdClose,
    MdCloudUpload,
    MdExpandMore,
    MdOndemandVideo,
    MdPhotoLibrary,
} from 'react-icons/md'
import { toast } from 'react-toastify'
import {
    addCatalogYoutubeMedia,
    CATALOG_MEDIA_MAX_IMAGES,
    CATALOG_MEDIA_MAX_UPLOADED_VIDEOS,
    confirmCatalogMedia,
    createCatalogMediaIntent,
    getCatalogMedia,
    removeCatalogMedia,
    reorderCatalogMedia,
    type CatalogMedia,
} from '@/lib/api/catalog/catalog-api'
import { uploadToSignedUrl } from '@/lib/api/storage/storage-api'
import { useImageCrop } from '@/components/image-crop'

interface CatalogMediaSectionProps {
    /** Item já salvo. Sem id, a galeria trabalha em modo pendente (ver abaixo). */
    catalogId?: number | null
    /** Permissão de escrita do catálogo; sem ela a galeria é só leitura. */
    canEdit?: boolean
    /** Rótulo do que está sendo cadastrado, para o texto de apoio ("produto", "item"). */
    subjectLabel?: string
}

/** Mídia escolhida antes de o item existir. Só vira `CatalogMedia` após o save. */
interface PendingMedia {
    /** Chave local da lista — o id real só existe depois do envio. */
    key: string
    file?: File
    /** Preview local (`URL.createObjectURL`) das imagens escolhidas. */
    previewUrl?: string
    /** Link colado, ainda não validado pelo backend. */
    youtubeUrl?: string
    kind: 'IMAGE' | 'VIDEO'
}

export interface CatalogMediaSectionHandle {
    /**
     * Envia o que ficou pendente para o item recém-criado. O formulário chama
     * isto logo depois do POST, com o id que acabou de nascer.
     */
    uploadPending: (catalogId: number) => Promise<void>
    /** Há mídia escolhida e ainda não enviada? */
    hasPending: () => boolean
}

/** `youtu.be/ID`, `watch?v=ID`, `embed/ID`, `shorts/ID`. Só para a miniatura local. */
function extractYoutubeId(url: string): string | null {
    const match = url.match(
        /(?:youtu\.be\/|v=|\/embed\/|\/shorts\/)([\w-]{11})/
    )
    return match ? match[1] : null
}

/**
 * Galeria de fotos e vídeos de um item do catálogo.
 *
 * Fica recolhida por padrão: a maioria dos itens não tem mídia, e um formulário
 * que já é longo não deve crescer mais por causa de um recurso opcional. A
 * galeria só é buscada quando a seção é aberta — por isso o contador do cabeçalho
 * aparece a partir da primeira abertura, e não no carregamento da tela.
 *
 * **Cadastro novo:** o upload precisa de um `catalogId` (é nele que a mídia é
 * pendurada), mas exigir "salve primeiro" quebra o cadastro de produto, onde a
 * foto faz parte do próprio ato de cadastrar. Então, sem id, os arquivos ficam
 * RETIDOS no cliente com preview local, e o formulário os despacha via
 * `uploadPending(id)` assim que o item nasce.
 */
const CatalogMediaSection = forwardRef<
    CatalogMediaSectionHandle,
    CatalogMediaSectionProps
>(function CatalogMediaSection(
    { catalogId, canEdit = true, subjectLabel = 'item' },
    ref
) {
    const [open, setOpen] = useState(false)
    const [media, setMedia] = useState<CatalogMedia[]>([])
    const [pending, setPending] = useState<PendingMedia[]>([])
    const [loading, setLoading] = useState(false)
    const [uploading, setUploading] = useState(false)
    // Foto de produto: quadrada por padrão (o card dos temas aceita retrato e
    // paisagem, então as outras proporções ficam como atalho).
    const { cropFiles, cropDialog } = useImageCrop({
        aspects: [
            { label: 'Quadrada', value: 1 },
            { label: 'Retrato (3:4)', value: 3 / 4 },
            { label: 'Paisagem (4:3)', value: 4 / 3 },
            { label: 'Livre' },
        ],
        output: { maxWidth: 1600 },
    })
    const [progress, setProgress] = useState(0)
    const [dragOver, setDragOver] = useState(false)
    const [youtubeUrl, setYoutubeUrl] = useState('')
    const [addingYoutube, setAddingYoutube] = useState(false)
    const [draggingId, setDraggingId] = useState<number | null>(null)

    const fileInputRef = useRef<HTMLInputElement>(null!)

    const images = media.filter(m => m.kind === 'IMAGE')
    const uploadedVideos = media.filter(
        m => m.kind === 'VIDEO' && m.provider === 'UPLOAD'
    )
    const pendingImages = pending.filter(p => p.kind === 'IMAGE')
    const pendingUploadedVideos = pending.filter(
        p => p.kind === 'VIDEO' && p.file
    )

    const load = useCallback(async () => {
        if (!catalogId) return
        setLoading(true)
        try {
            const response = await getCatalogMedia(catalogId)
            setMedia(response.result ?? [])
        } finally {
            setLoading(false)
        }
    }, [catalogId])

    // Só busca quando a seção é aberta: item sem mídia é a regra, e uma chamada
    // por item carregado na tela não se paga.
    useEffect(() => {
        if (open) load()
    }, [open, load])

    /** Envia de fato para um item que já existe. Usado no fluxo normal e no flush. */
    const sendFiles = async (
        targetId: number,
        files: File[],
        collect?: (created: CatalogMedia[]) => void
    ) => {
        setUploading(true)
        setProgress(0)
        try {
            // 1. Autoriza. Se algum limite estourar, nada sobe.
            const intents = await createCatalogMediaIntent(
                targetId,
                files.map(f => ({
                    name: f.name,
                    mimeType: f.type || 'application/octet-stream',
                    sizeBytes: f.size,
                }))
            )
            if (!intents.success || !intents.result?.length) return

            // 2. Envia direto ao R2. O progresso é do conjunto, não de cada
            // arquivo: quem envia cinco fotos quer saber quanto falta no total.
            const sent: string[] = []
            const totalBytes = files.reduce((sum, f) => sum + f.size, 0) || 1
            let uploadedBytes = 0

            for (let i = 0; i < intents.result.length; i++) {
                const intent = intents.result[i]
                const file = files[i]

                await uploadToSignedUrl(
                    intent.uploadUrl,
                    file,
                    file.type || 'application/octet-stream',
                    percent => {
                        const current = (percent / 100) * file.size
                        setProgress(
                            Math.min(
                                99,
                                Math.round(
                                    ((uploadedBytes + current) / totalBytes) *
                                        100
                                )
                            )
                        )
                    }
                )

                uploadedBytes += file.size
                sent.push(intent.objectKey)
            }

            // 3. Confirma. Só agora as mídias existem para o item.
            const confirmed = await confirmCatalogMedia(targetId, sent)
            if (confirmed.success && confirmed.result) {
                if (collect) collect(confirmed.result)
                else setMedia(current => [...current, ...confirmed.result!])
                setProgress(100)
                toast.success(
                    files.length === 1
                        ? 'Arquivo enviado'
                        : `${files.length} arquivos enviados`
                )
            }
        } catch (error) {
            toast.error((error as Error).message || 'Falha no envio')
        } finally {
            setUploading(false)
            setProgress(0)
            if (fileInputRef.current) fileInputRef.current.value = ''
        }
    }

    const handleFiles = async (rawFiles: File[]) => {
        if (!rawFiles.length) return

        // Corte antes de tudo: cada foto passa pelo enquadramento (cancelar pula
        // aquela foto); vídeo e GIF seguem como vieram.
        const files = await cropFiles(rawFiles)
        if (!files.length) {
            if (fileInputRef.current) fileInputRef.current.value = ''
            return
        }

        const incomingImages = files.filter(f =>
            f.type.startsWith('image/')
        ).length
        const incomingVideos = files.filter(f =>
            f.type.startsWith('video/')
        ).length

        // Barra no cliente o que o servidor barraria de qualquer forma — evita
        // subir 300 MB para receber "máximo de 10 fotos" no fim do upload. Os
        // pendentes contam: eles vão para o mesmo item assim que ele nascer.
        if (
            images.length + pendingImages.length + incomingImages >
            CATALOG_MEDIA_MAX_IMAGES
        ) {
            toast.error(`Máximo de ${CATALOG_MEDIA_MAX_IMAGES} fotos por item.`)
            return
        }
        if (
            uploadedVideos.length +
                pendingUploadedVideos.length +
                incomingVideos >
            CATALOG_MEDIA_MAX_UPLOADED_VIDEOS
        ) {
            toast.error(
                `Máximo de ${CATALOG_MEDIA_MAX_UPLOADED_VIDEOS} vídeos enviados. Para mais vídeos, use o YouTube.`
            )
            return
        }

        // Item ainda não existe: retém com preview local e espera o save.
        if (!catalogId) {
            setPending(current => [
                ...current,
                ...files.map((file, index) => ({
                    key: `${file.name}-${file.size}-${index}-${current.length}`,
                    file,
                    previewUrl: file.type.startsWith('image/')
                        ? URL.createObjectURL(file)
                        : undefined,
                    kind: file.type.startsWith('image/')
                        ? ('IMAGE' as const)
                        : ('VIDEO' as const),
                })),
            ])
            if (fileInputRef.current) fileInputRef.current.value = ''
            return
        }

        await sendFiles(catalogId, files)
    }

    const handleAddYoutube = async () => {
        if (!youtubeUrl.trim()) return

        // Item ainda não existe: guarda o link e valida no envio (quem diz se o
        // link presta é o backend, e ele precisa do item para responder).
        if (!catalogId) {
            setPending(current => [
                ...current,
                {
                    key: `yt-${youtubeUrl.trim()}-${current.length}`,
                    youtubeUrl: youtubeUrl.trim(),
                    kind: 'VIDEO' as const,
                },
            ])
            setYoutubeUrl('')
            return
        }

        setAddingYoutube(true)
        try {
            const response = await addCatalogYoutubeMedia(
                catalogId,
                youtubeUrl.trim()
            )
            // `success`, e não a presença de `result`: em erro o cliente HTTP ainda
            // devolve o envelope, então checar `result` dava "Vídeo adicionado" para
            // uma duplicata que o backend tinha recusado.
            if (response.success && response.result) {
                setMedia(current => [...current, response.result!])
                setYoutubeUrl('')
                toast.success('Vídeo adicionado')
            }
        } finally {
            setAddingYoutube(false)
        }
    }

    /** Despacha o que ficou retido assim que o item ganha id. */
    const uploadPending = useCallback(
        async (targetId: number) => {
            if (!pending.length) return

            const files = pending.map(p => p.file).filter((f): f is File => !!f)
            const links = pending
                .map(p => p.youtubeUrl)
                .filter((u): u is string => !!u)

            if (files.length) {
                await sendFiles(targetId, files, () => {})
            }
            for (const link of links) {
                await addCatalogYoutubeMedia(targetId, link)
            }

            pending.forEach(p => {
                if (p.previewUrl) URL.revokeObjectURL(p.previewUrl)
            })
            setPending([])
        },
        // `sendFiles` é recriado a cada render e não guarda estado próprio.
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [pending]
    )

    useImperativeHandle(
        ref,
        () => ({
            uploadPending,
            hasPending: () => pending.length > 0,
        }),
        [uploadPending, pending.length]
    )

    // Preview local segura um blob na memória até ser revogado.
    useEffect(() => {
        return () => {
            pending.forEach(p => {
                if (p.previewUrl) URL.revokeObjectURL(p.previewUrl)
            })
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    const handleRemovePending = (key: string) => {
        setPending(current => {
            const target = current.find(p => p.key === key)
            if (target?.previewUrl) URL.revokeObjectURL(target.previewUrl)
            return current.filter(p => p.key !== key)
        })
    }

    const handleRemove = async (item: CatalogMedia) => {
        if (!catalogId) return
        const previous = media
        setMedia(current => current.filter(m => m.id !== item.id))
        const response = await removeCatalogMedia(catalogId, item.id)
        // Erro já vira toast no cliente HTTP; aqui só desfazemos o otimismo.
        if (!response.success) setMedia(previous)
    }

    /** Solta o item arrastado na posição do alvo e persiste a ordem inteira. */
    const handleDropOnItem = async (targetId: number) => {
        if (!catalogId || draggingId == null || draggingId === targetId) return

        const reordered = [...media]
        const from = reordered.findIndex(m => m.id === draggingId)
        const to = reordered.findIndex(m => m.id === targetId)
        if (from < 0 || to < 0) return

        const [moved] = reordered.splice(from, 1)
        reordered.splice(to, 0, moved)

        const previous = media
        setMedia(reordered)
        setDraggingId(null)

        const response = await reorderCatalogMedia(
            catalogId,
            reordered.map(m => m.id)
        )
        if (!response.success) setMedia(previous)
    }

    const onDrop = (event: React.DragEvent) => {
        event.preventDefault()
        setDragOver(false)
        if (!canEdit) return

        const files = Array.from(event.dataTransfer.files).filter(
            f => f.type.startsWith('image/') || f.type.startsWith('video/')
        )
        if (files.length) handleFiles(files)
    }

    const count = media.length + pending.length

    return (
        <div className="bg-base-100 rounded-xl border border-base-300/50 shadow-sm">
            <button
                type="button"
                onClick={() => setOpen(o => !o)}
                className="flex w-full items-center justify-between gap-3 p-6 text-left"
            >
                <div className="flex items-center gap-3">
                    <MdPhotoLibrary className="text-xl text-base-content/60" />
                    <div>
                        <h2 className="text-lg font-semibold">
                            Fotos e vídeos
                        </h2>
                        <p className="text-sm text-base-content/60">
                            {count > 0
                                ? `${count} ${count === 1 ? 'item' : 'itens'} na galeria`
                                : `Opcional — mostre o ${subjectLabel} a quem vai escolher`}
                        </p>
                    </div>
                </div>
                <MdExpandMore
                    className={`text-2xl text-base-content/50 transition-transform ${
                        open ? 'rotate-180' : ''
                    }`}
                />
            </button>

            {open && (
                <div className="border-t border-base-300/50 p-6 pt-5">
                    {
                        <>
                            {canEdit && (
                                <>
                                    <div
                                        onDragOver={e => {
                                            e.preventDefault()
                                            setDragOver(true)
                                        }}
                                        onDragLeave={() => setDragOver(false)}
                                        onDrop={onDrop}
                                        onClick={() =>
                                            fileInputRef.current?.click()
                                        }
                                        className={`flex cursor-pointer flex-col items-center gap-2 rounded-xl border-2 border-dashed p-8 text-center transition-colors ${
                                            dragOver
                                                ? 'border-primary bg-primary/5'
                                                : 'border-base-300 hover:border-primary/50 hover:bg-base-200/40'
                                        }`}
                                    >
                                        {uploading ? (
                                            <span className="loading loading-spinner loading-md text-primary" />
                                        ) : (
                                            <MdCloudUpload className="text-3xl text-base-content/40" />
                                        )}
                                        <p className="text-sm font-medium">
                                            {uploading
                                                ? `Enviando... ${progress}%`
                                                : 'Arraste as fotos e vídeos aqui ou clique para escolher'}
                                        </p>
                                        {uploading ? (
                                            <progress
                                                className="progress progress-primary w-56"
                                                value={progress}
                                                max={100}
                                            />
                                        ) : (
                                            <p className="text-xs text-base-content/50">
                                                Até {CATALOG_MEDIA_MAX_IMAGES}{' '}
                                                fotos e{' '}
                                                {
                                                    CATALOG_MEDIA_MAX_UPLOADED_VIDEOS
                                                }{' '}
                                                vídeos. Vídeo do YouTube não tem
                                                limite. O tamanho máximo por
                                                arquivo é o do seu plano.
                                            </p>
                                        )}
                                    </div>

                                    <input
                                        ref={fileInputRef}
                                        type="file"
                                        accept="image/*,video/*"
                                        multiple
                                        className="hidden"
                                        onChange={e =>
                                            handleFiles(
                                                Array.from(e.target.files ?? [])
                                            )
                                        }
                                    />
                                    {cropDialog}

                                    <div className="mt-4 flex flex-col gap-2 sm:flex-row">
                                        {/* O nome do campo vai em `aria-label` no input, e
                                            não num `<label class="sr-only">`: `sr-only` é
                                            `position:absolute`, e sem ancestral posicionado
                                            ele escapa até o topo do documento. Dentro de um
                                            `<main>` rolável isso estica o scroll da PÁGINA
                                            para a altura do formulário inteiro — ao rolar
                                            até o fim, o scroll passava do `main` para o
                                            documento e sobrava uma área branca enorme
                                            abaixo do layout. */}
                                        <div className="relative flex-1">
                                            <MdOndemandVideo className="absolute z-10 left-3 top-1/2 -translate-y-1/2 text-lg text-base-content/40" />
                                            <input
                                                id="catalog-youtube"
                                                type="text"
                                                aria-label="Link do YouTube"
                                                className="input input-bordered w-full pl-10"
                                                placeholder="Cole um link do YouTube (sem limite de quantidade)"
                                                value={youtubeUrl}
                                                onChange={e =>
                                                    setYoutubeUrl(
                                                        e.target.value
                                                    )
                                                }
                                                onKeyDown={e => {
                                                    if (e.key === 'Enter') {
                                                        e.preventDefault()
                                                        handleAddYoutube()
                                                    }
                                                }}
                                            />
                                        </div>
                                        <button
                                            type="button"
                                            className="btn btn-outline"
                                            disabled={
                                                !youtubeUrl.trim() ||
                                                addingYoutube
                                            }
                                            onClick={handleAddYoutube}
                                        >
                                            {addingYoutube ? (
                                                <span className="loading loading-spinner loading-sm" />
                                            ) : (
                                                <MdAdd className="text-lg" />
                                            )}
                                            Adicionar vídeo
                                        </button>
                                    </div>
                                </>
                            )}

                            {loading ? (
                                <div className="mt-6 flex justify-center">
                                    <span className="loading loading-spinner loading-md text-primary" />
                                </div>
                            ) : media.length > 0 ? (
                                <>
                                    <div className="mt-6 grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4">
                                        {media.map(item => (
                                            <div
                                                key={item.id}
                                                draggable={canEdit}
                                                onDragStart={() =>
                                                    setDraggingId(item.id)
                                                }
                                                onDragEnd={() =>
                                                    setDraggingId(null)
                                                }
                                                onDragOver={e =>
                                                    e.preventDefault()
                                                }
                                                onDrop={() =>
                                                    handleDropOnItem(item.id)
                                                }
                                                className={`group relative aspect-square overflow-hidden rounded-lg border border-base-300 bg-base-200 ${
                                                    canEdit ? 'cursor-move' : ''
                                                } ${draggingId === item.id ? 'opacity-40' : ''}`}
                                            >
                                                <MediaThumbnail item={item} />

                                                {canEdit && (
                                                    <button
                                                        type="button"
                                                        onClick={() =>
                                                            handleRemove(item)
                                                        }
                                                        className="btn btn-circle btn-xs btn-error absolute right-1.5 top-1.5 opacity-0 transition-opacity group-hover:opacity-100"
                                                        aria-label="Remover"
                                                    >
                                                        <MdClose />
                                                    </button>
                                                )}
                                            </div>
                                        ))}
                                    </div>

                                    {canEdit && media.length > 1 && (
                                        <p className="mt-3 text-xs text-base-content/50">
                                            Arraste para reordenar. O primeiro
                                            item é a capa.
                                        </p>
                                    )}
                                </>
                            ) : pending.length === 0 ? (
                                <p className="mt-6 text-sm text-base-content/50">
                                    Nenhuma foto ou vídeo ainda.
                                </p>
                            ) : null}

                            {/* Escolhidos e ainda não enviados. Não são arrastáveis:
                                a ordem só existe depois que o item nasce. */}
                            {pending.length > 0 && (
                                <>
                                    <div className="mt-6 grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4">
                                        {pending.map(item => (
                                            <div
                                                key={item.key}
                                                className="group relative aspect-square overflow-hidden rounded-lg border border-dashed border-primary/40 bg-base-200"
                                            >
                                                <PendingThumbnail item={item} />

                                                <span className="absolute bottom-1 left-1 badge badge-xs badge-primary">
                                                    a enviar
                                                </span>

                                                {canEdit && (
                                                    <button
                                                        type="button"
                                                        onClick={() =>
                                                            handleRemovePending(
                                                                item.key
                                                            )
                                                        }
                                                        className="btn btn-circle btn-xs btn-error absolute right-1.5 top-1.5 opacity-0 transition-opacity group-hover:opacity-100"
                                                        aria-label="Remover"
                                                    >
                                                        <MdClose />
                                                    </button>
                                                )}
                                            </div>
                                        ))}
                                    </div>
                                    <p className="mt-3 text-xs text-base-content/50">
                                        {pending.length === 1
                                            ? '1 arquivo será enviado ao salvar.'
                                            : `${pending.length} arquivos serão enviados ao salvar.`}
                                    </p>
                                </>
                            )}
                        </>
                    }
                </div>
            )}
        </div>
    )
})

export default CatalogMediaSection

/** Miniatura do que ainda não subiu: preview local da imagem, ou capa do YouTube. */
function PendingThumbnail({ item }: { item: PendingMedia }) {
    if (item.previewUrl) {
        return (
            <img
                src={item.previewUrl}
                alt={item.file?.name ?? ''}
                className="h-full w-full object-cover"
            />
        )
    }

    const youtubeId = item.youtubeUrl ? extractYoutubeId(item.youtubeUrl) : null
    if (youtubeId) {
        return (
            <>
                <img
                    src={`https://img.youtube.com/vi/${youtubeId}/mqdefault.jpg`}
                    alt="Vídeo do YouTube"
                    className="h-full w-full object-cover"
                    loading="lazy"
                />
                <span className="absolute inset-0 flex items-center justify-center bg-black/30">
                    <MdOndemandVideo className="text-3xl text-white drop-shadow" />
                </span>
            </>
        )
    }

    return (
        <span className="flex h-full w-full flex-col items-center justify-center gap-1 px-2 text-center text-base-content/50">
            <MdOndemandVideo className="text-3xl" />
            <span className="text-xs">
                {item.file?.name || item.youtubeUrl}
            </span>
        </span>
    )
}

/**
 * Miniatura. O vídeo do YouTube usa a capa servida pelo próprio YouTube em vez de
 * um `<iframe>`: numa grade de doze itens seriam doze players carregando ao mesmo
 * tempo, e a galeria do formulário só precisa identificar o vídeo.
 */
function MediaThumbnail({ item }: { item: CatalogMedia }) {
    if (item.kind === 'IMAGE' && item.url) {
        return (
            <img
                src={item.url}
                alt={item.title ?? ''}
                className="h-full w-full object-cover"
                loading="lazy"
            />
        )
    }

    if (item.provider === 'YOUTUBE' && item.videoId) {
        return (
            <>
                <img
                    src={`https://img.youtube.com/vi/${item.videoId}/mqdefault.jpg`}
                    alt={item.title ?? 'Vídeo do YouTube'}
                    className="h-full w-full object-cover"
                    loading="lazy"
                />
                <span className="absolute inset-0 flex items-center justify-center bg-black/30">
                    <MdOndemandVideo className="text-3xl text-white drop-shadow" />
                </span>
            </>
        )
    }

    return (
        <span className="flex h-full w-full flex-col items-center justify-center gap-1 text-base-content/50">
            <MdOndemandVideo className="text-3xl" />
            <span className="px-2 text-center text-xs">
                {item.title || 'Vídeo'}
            </span>
        </span>
    )
}
